import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useDispatch } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import type { AppDispatch } from '../../store/store';
import type { CropAttributeSummary, CropAttribute } from '../../models/Crop';
import { resetEditDraft, setEditDraft, updateEditDraft } from '../../store/slices/farmAttributeFormsSlice';
import { useGetCropAttributeByIdQuery } from '../../api/cropsApi';
import { mapAttributeToFormValues, normalizeAttributeFormValues } from './attributeUtils';
import { useUpdateCropAttributeMutation } from '../../api/cropsApi';
import { toastError, toastSuccess } from '../../utils/toast';

export const useAttributeEditor = (onSuccess?: () => void, saveId?: string) => {
  const [editForm] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAttributeId, setEditingAttributeId] = useState<number | string | null>(null);
  const [updateCropAttribute] = useUpdateCropAttributeMutation();
  const suppressFormSyncRef = useRef(false);

  const hydrateEditForm = useCallback(
    (attribute: CropAttributeSummary | CropAttribute) => {
      const draft = mapAttributeToFormValues(attribute);
      suppressFormSyncRef.current = true;
      dispatch(setEditDraft({ attributeId: attribute.id, form: draft }));
      editForm.setFieldsValue(draft);
      // Используем setTimeout вместо Promise.resolve для более надежного снятия блокировки
      setTimeout(() => {
        suppressFormSyncRef.current = false;
      }, 100);
    },
    [dispatch, editForm],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingAttributeId(null);
    setCurrentAttributeId(null);
    setSkip(true); // Останавливаем запросы
    dispatch(resetEditDraft());
    editForm.resetFields();
  }, [dispatch, editForm]);

  const handleEditFormValuesChange = useCallback(
    (_: any, allValues: any) => {
      if (!editModalOpen) return;
      if (suppressFormSyncRef.current) return;
      dispatch(updateEditDraft(normalizeAttributeFormValues(allValues)));
    },
    [dispatch, editModalOpen],
  );

  const handleUpdateFarmAttribute = useCallback(async () => {
    if (!editingAttributeId) return;
    try {
      const values = await editForm.validateFields();
      const options = values.options?.map((option: any, index: number) => ({
        ...option,
        sortOrder: index,
      })).filter((option: any) => option.title?.trim());

      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        options: options?.length ? options : null,
      };

      if (!saveId) {
        toastError('Активный сейв не определён');
        return;
      }
      await updateCropAttribute({
        id: editingAttributeId,
        saveId,
        payload,
      }).unwrap();
      toastSuccess('Атрибут обновлён');
      handleCloseEditModal();
      onSuccess?.();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  }, [editForm, editingAttributeId, handleCloseEditModal, onSuccess, updateCropAttribute, saveId]);

  const [skip, setSkip] = useState(true);
  const [currentAttributeId, setCurrentAttributeId] = useState<string | number | null>(null);

  const { data: fullAttribute, isError, isLoading } = useGetCropAttributeByIdQuery(
    currentAttributeId != null && saveId
      ? { id: currentAttributeId, saveId }
      : skipToken,
    { skip }
  );

  useEffect(() => {
    // Проверяем, что данные соответствуют текущему запрошенному атрибуту
    if (fullAttribute && String(fullAttribute.id) === String(currentAttributeId)) {
      hydrateEditForm(fullAttribute);
      setSkip(true); // Прекращаем запросы после получения данных
      setEditModalOpen(true); // Открываем модалку только ПОСЛЕ загрузки правильных данных
    } else if (isError && editingAttributeId) {
      // В случае ошибки используем существующие данные из summary
      const attribute = attributeRef.current;
      if (attribute) {
        console.warn('Failed to fetch full attribute data, using summary data');
        hydrateEditForm(attribute);
        setEditModalOpen(true); // Открываем модалку даже при ошибке, используя summary данные
      }
    }
  }, [fullAttribute, isError, hydrateEditForm, editingAttributeId, currentAttributeId]);

  const attributeRef = useRef<CropAttributeSummary | null>(null);

  const handleOpenEditAttribute = useCallback(
    (attribute: CropAttributeSummary) => {
      // Закрываем модалку если она была открыта
      setEditModalOpen(false);
      
      // Очищаем форму и сбрасываем состояние
      editForm.resetFields();
      dispatch(resetEditDraft());
      
      // Затем устанавливаем новое состояние и запускаем загрузку
      setEditingAttributeId(attribute.id);
      attributeRef.current = attribute;
      setCurrentAttributeId(attribute.id);
      setSkip(false); // Запускаем запрос данных
      // НЕ открываем модалку здесь - она откроется в useEffect после загрузки данных
    },
    [editForm, dispatch],
  );

  return {
    editForm,
    editModalOpen,
    editingAttributeId,
    isLoadingAttribute: isLoading,
    handleCloseEditModal,
    handleEditFormValuesChange,
    handleOpenEditAttribute,
    handleUpdateFarmAttribute,
  };
};